import React from 'react';
import { Search, Star, Heart } from 'lucide-react';

export default function ProductsGrid({
  isDark,
  categories,
  products,
  searchQuery,
  activeCategory,
  favorites,
  onSearch,
  onChangeCategory,
  onToggleFavorite,
  onAddToCart,
}) {
  const filteredProducts = products.filter((product) => {
    const matchesCategory =
      activeCategory === 'всі' || product.category === activeCategory;
    const matchesSearch =
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <section
      id='products'
      className={`py-20 px-4 ${
        isDark ? 'bg-gray-800/50' : 'bg-white/50'
      } backdrop-blur-sm`}
    >
      <div className='max-w-7xl mx-auto'>
        <div className='text-center mb-16'>
          <h2
            className={`text-5xl font-bold mb-6 ${
              isDark
                ? 'text-white'
                : 'bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent'
            }`}
          >
            Наші свічки ✨
          </h2>
          <p
            className={`text-xl ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            } max-w-2xl mx-auto`}
          >
            Кожна свічка створена з любов'ю та натуральних матеріалів
          </p>
        </div>

        <div className='mb-12 space-y-6'>
          <div className='relative max-w-xl mx-auto'>
            <Search
              className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
            <input
              type='text'
              placeholder='Пошук свічок...'
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              className={`w-full pl-12 pr-4 py-4 rounded-2xl border transition-all duration-300 focus:outline-none focus:ring-4 ${
                isDark
                  ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-orange-400 focus:ring-orange-400/20'
                  : 'bg-white border-orange-200 text-gray-900 placeholder-gray-500 focus:border-orange-400 focus:ring-orange-200'
              }`}
            />
          </div>

          <div className='flex flex-wrap justify-center gap-3'>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => onChangeCategory(category.id)}
                className={`px-5 py-3 rounded-2xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg transform scale-105'
                    : isDark
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                    : 'bg-white text-gray-600 hover:bg-orange-50 border border-orange-200'
                }`}
              >
                <span className='text-lg'>{category.emoji}</span>
                <span className='font-semibold'>{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
          {filteredProducts.map((product) => (
            <div key={product.id} className='group relative'>
              <div
                className={`relative rounded-2xl p-6 transition-all duration-500 transform hover:scale-105 ${
                  isDark
                    ? 'bg-gray-800 border border-gray-700 hover:shadow-2xl'
                    : 'bg-white border border-orange-200 hover:shadow-xl'
                } overflow-hidden`}
              >
                <button
                  onClick={() => onToggleFavorite(product.id)}
                  className={`absolute top-4 right-4 p-2 rounded-full transition-all duration-300 z-10 ${
                    isDark
                      ? 'bg-gray-700 hover:bg-gray-600'
                      : 'bg-white hover:bg-gray-50'
                  } shadow-lg hover:scale-110`}
                >
                  <Heart
                    className={`w-5 h-5 transition-colors ${
                      favorites.includes(product.id)
                        ? 'text-red-500 fill-red-500'
                        : isDark
                        ? 'text-gray-400 hover:text-red-400'
                        : 'text-gray-400 hover:text-red-500'
                    }`}
                  />
                </button>

                <div className='text-center mb-6'>
                  <div className='text-7xl mb-4 group-hover:scale-110 transition-transform duration-300'>
                    {product.emoji}
                  </div>
                  <h3
                    className={`text-xl font-bold mb-2 ${
                      isDark ? 'text-white' : 'text-gray-800'
                    }`}
                  >
                    {product.name}
                  </h3>
                  <p
                    className={`${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    } mb-4`}
                  >
                    {product.description}
                  </p>
                </div>

                <div className='flex items-center justify-center gap-2 mb-4'>
                  <div className='flex'>
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(product.rating)
                            ? 'text-yellow-400 fill-yellow-400'
                            : isDark
                            ? 'text-gray-600'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span
                    className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    {product.rating} ({product.reviews})
                  </span>
                </div>

                <div className='flex justify-center gap-2 mb-6'>
                  {product.colors.map((color, index) => (
                    <span
                      key={index}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        isDark
                          ? 'bg-orange-900/50 text-orange-300 border border-orange-700'
                          : 'bg-orange-100 text-orange-600 border border-orange-200'
                      }`}
                    >
                      {color}
                    </span>
                  ))}
                </div>

                <div className='text-center mb-6'>
                  <div className='flex items-center justify-center gap-3'>
                    <span
                      className={`text-2xl font-bold ${
                        isDark ? 'text-orange-400' : 'text-orange-600'
                      }`}
                    >
                      {product.price}₴
                    </span>
                    {product.oldPrice && (
                      <span
                        className={`text-lg line-through ${
                          isDark ? 'text-gray-500' : 'text-gray-400'
                        }`}
                      >
                        {product.oldPrice}₴
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => onAddToCart(product)}
                  disabled={!product.inStock}
                  className={`w-full py-3 rounded-2xl font-bold transition-all duration-300 ${
                    product.inStock
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-red-500 hover:to-pink-500 transform hover:scale-105 shadow-lg'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  {product.inStock ? 'Додати в кошик' : 'Немає в наявності'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
